/* eslint-disable @atlaskit/design-system/use-primitives-text */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import gridBackground from '../../../../../assets/grid-background.svg';
import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../../hooks/use-user-context';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { CollaborationUsersAtom } from '../../../../state';
import { SpacingWrapper } from '../../../SpacingWrapper';
import { STATIC_COLLABORATORS_DATA } from '../static-data';

import blueBackground from './assets/blue-background.webp';
import blueMask from './assets/blue-mask.svg';
import greyBackground from './assets/grey-background.webp';
import greyMask from './assets/grey-mask.svg';
import limeBackground from './assets/lime-background.webp';
import limeMask from './assets/lime-mask.svg';
import purpleBackground from './assets/purple-background.webp';
import purpleMask from './assets/purple-mask.svg';
import yellowBackground from './assets/yellow-background.webp';
import yellowMask from './assets/yellow-mask.svg';
import { messages } from './messages';

const RANK_CARDS = [
	{ backgroundColor: '#82B536', backgroundSrc: limeBackground, maskSrc: limeMask },
	{ backgroundColor: '#BF63F3', backgroundSrc: purpleBackground, maskSrc: purpleMask },
	{ backgroundColor: '#4D8CED', backgroundSrc: blueBackground, maskSrc: blueMask },
	{ backgroundColor: '#FCA700', backgroundSrc: yellowBackground, maskSrc: yellowMask },
	{ backgroundColor: '#8C8F97', backgroundSrc: greyBackground, maskSrc: greyMask },
] as const;

const styles = cssMap({
	section: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
	},
	contentWrapper: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		paddingInline: token('space.400'),
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.600'),
	},
	statsRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		paddingInline: token('space.400'),
		paddingBlock: token('space.600'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	bigNumber: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '160px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1,
		margin: 0,
	},
	bigNumberLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
		font: token('font.body.large'),
		margin: 0,
	},
	bigNumberDescription: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#6B6E76',
		fontFamily: token('font.family.brand.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '16px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		margin: 0,
		width: '100%',
		maxWidth: '800px',
	},
	collaboratorsList: {
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	collaboratorRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.400'),
	},
	avatarWrapper: {
		width: '120px',
		height: '120px',
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
	},
	avatar: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		flexShrink: 0,
		borderRadius: token('radius.xsmall'),
	},
	collaboratorInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		flex: '1 1 auto',
	},
	collaboratorName: {
		color: token('color.text'),
		font: token('font.body.large'),
		margin: 0,
	},
	collaboratorSharedItems: {
		color: token('color.text.subtlest'),
		font: token('font.body.large'),
		margin: 0,
	},
	collaboratorScoreWrapper: {
		textAlign: 'right',
		paddingRight: token('space.400'),
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	collaboratorScoreLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.code'),
		color: token('color.text.subtlest'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
	collaboratorScore: {
		font: token('font.body'),
		textAlign: 'right',
		margin: 0,
	},
});

type CollaboratorsSectionProps = {
	metrics: OrgMetricsResponse;
};

export const CollaboratorsSection = ({
	metrics,
}: CollaboratorsSectionProps): JSX.Element | null => {
	const { formatNumber } = useIntl();
	const { data: collaborators, error } = useAtomValue(CollaborationUsersAtom);
	const { totalCollaborators } = STATIC_COLLABORATORS_DATA;
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.Collaborators);
	const { identity: currentUser } = useAtlassianUserContext();

	// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
	if (error !== null) {
		return (
			<div>
				<FormattedMessage {...messages.loadCollaboratorsError} />
			</div>
		);
	}

	const rankedCollaborators = collaborators
		.filter((user) => user.id !== currentUser.id)
		.slice(0, RANK_CARDS.length);

	// TODO: GRAPHYTE-2516: no collaborators to show
	if (rankedCollaborators.length <= 2) {
		// eslint-disable-next-line no-console
		console.log('less than 3 collaborators to show');
		return null;
	}

	return (
		<section
			ref={sectionRef}
			css={styles.section}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ backgroundImage: `url(${gridBackground})` }}
		>
			<SpacingWrapper>
				<div css={styles.contentWrapper}>
					<div css={styles.content}>
						<div css={styles.statsRow}>
							<p css={styles.bigNumberLabel}>
								<FormattedMessage {...messages.totalCollaboratorsLabel} />
							</p>
							<p css={styles.bigNumber}>{formatNumber(totalCollaborators)}</p>
							<p css={styles.bigNumberDescription}>
								<FormattedMessage {...messages.totalCollaboratorsDescription} />
							</p>
						</div>
						<div css={styles.collaboratorsList}>
							{collaborators.map((collaborator) => (
								<div key={collaborator.expand.name} css={styles.collaboratorRow}>
									<div css={styles.avatarWrapper}>
										<img
											css={styles.avatar}
											src={collaborator.expand.picture}
											alt={collaborator.expand.name}
										/>
									</div>
									<div css={styles.collaboratorInfo}>
										<p css={styles.collaboratorName}>{collaborator.expand.name}</p>
										<p css={styles.collaboratorSharedItems}>
											<FormattedMessage
												{...messages.sharedItemsLabel}
												values={{
													count: 1234, // not available yet
												}}
											/>
										</p>
									</div>
									<div css={styles.collaboratorScoreWrapper}>
										<p css={styles.collaboratorScoreLabel}>
											<FormattedMessage {...messages.collaborationScoreLabel} />
										</p>
										<p css={styles.collaboratorScore}>{collaborator.score}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</SpacingWrapper>
		</section>
	);
};
